import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../contexts/ToastContext';
import ManualLink from '../components/ManualLink';
import './LoginScreen.css';

export default function LoginScreen() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [theme, setTheme] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [businessContent, setBusinessContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const { login, register, isLoggedIn, loading } = useAuth();
  const { showSuccess, showError } = useToastContext();
  const navigate = useNavigate();

  useEffect(() => {
    // 認証状態確認中は何もしない
    if (loading) return;
    
    if (isLoggedIn) {
      navigate('/mypage');
    }
  }, [isLoggedIn, loading, navigate]);

  // 認証状態確認中は何も表示しない（親のAppContentでローディング表示）
  if (loading) {
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || !password) {
      showError('電話番号とパスワードを入力してください');
      return;
    }

    setIsLoading(true);
    try {
      const success = await login(phone, password);
      if (success) {
        navigate('/mypage');
      } else {
        showError('電話番号またはパスワードが間違っています');
      }
    } catch (error) {
      showError('ログインに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !phone || !password || !theme) {
      showError('名前、電話番号、パスワード、テーマは必須項目です');
      return;
    }

    if (password.length !== 4) {
      showError('パスワードは4桁で入力してください');
      return;
    }

    setIsLoading(true);
    try {
      const success = await register(name, phone, password, theme, companyName, businessContent);
      if (success) {
        showSuccess('登録が完了しました！');
        navigate('/mypage');
      } else {
        showError('この電話番号は既に登録されています');
      }
    } catch (error) {
      showError('登録に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="manual-link-container">
        <ManualLink />
      </div>
      <div className="login-content">
        <div className="login-header">
          <div className="login-logo">
            <img src="/images/100-day_logo-20251014.png" alt="100日実践" className="logo-image" />
          </div>
          <h1 className="login-title">100日実践</h1>
          <p className="login-subtitle">みんなで励まし合って継続しよう</p>
        </div>

        {!showRegistration ? (
          <form className="login-form" onSubmit={handleLogin}>
            <div className="input-container">
              <label className="input-label">電話番号</label>
              <input
                type="tel"
                className="input-field"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="090-1234-5678"
                autoComplete="tel"
              />
            </div>

            <div className="input-container">
              <label className="input-label">パスワード（4桁）</label>
              <input
                type="password"
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="1234"
                maxLength={4}
              />
            </div>

            <button 
              type="submit"
              className={`login-button ${isLoading ? 'login-button-disabled' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={handleRegister}>
            <div className="input-container">
              <label className="input-label">お名前</label>
              <input
                type="text"
                className="input-field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="田中 太郎"
                autoComplete="name"
              />
            </div>

            <div className="input-container">
              <label className="input-label">電話番号</label>
              <input
                type="tel"
                className="input-field"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="090-1234-5678"
                autoComplete="tel"
              />
            </div>

            <div className="input-container">
              <label className="input-label">パスワード（4桁）</label>
              <input
                type="password"
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="1234"
                maxLength={4}
              />
            </div>

            <div className="input-container">
              <label className="input-label">100日実践するテーマ</label>
              <textarea
                className="input-field textarea-field"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="例：毎日30分ウォーキングをする"
                rows={3}
                maxLength={200}
              />
            </div>

            <div className="input-container">
              <label className="input-label">会社名（任意）</label>
              <input
                type="text"
                className="input-field"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="例：株式会社○○"
                maxLength={100}
              />
            </div>

            <div className="input-container">
              <label className="input-label">事業内容（任意）</label>
              <textarea
                className="input-field textarea-field"
                value={businessContent}
                onChange={(e) => setBusinessContent(e.target.value)}
                placeholder="例：Webシステム開発、アプリ開発、ITコンサルティング"
                rows={3}
                maxLength={500}
              />
            </div>

            <button 
              type="submit"
              className={`login-button ${isLoading ? 'login-button-disabled' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? '登録中...' : '新規登録'}
            </button>
          </form>
        )}

        <div className="form-switch">
          <button 
            type="button"
            className="switch-button"
            onClick={() => {
              setShowRegistration(!showRegistration);
              setPhone('');
              setPassword('');
              setName('');
              setTheme('');
              setCompanyName('');
              setBusinessContent('');
            }}
          >
            {showRegistration ? 'ログインに戻る' : '新規登録はこちら'}
          </button>
        </div>
      </div>

      <div className="login-footer">
        <p className="copyright">© 株式会社カフ．ウェブ．システム</p>
      </div>
    </div>
  );
}